'use client';

// Client half of the profile badge row: clickable pills that open a
// per-badge detail modal (big art, description, rarity, earned date), plus
// the "+N more" trigger for the full list. The server component
// (ProfileBadgeRow) computes collapse/sort and passes the display set here.

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BadgeModal from './BadgeModal';
import { BadgeArt, rarityLabel } from './badgeIcons';
import type { Badge } from './ProfileBadgeRow';

function fmt(d: string) {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function BadgePillRow({
  pills,
  allBadges,
  overflowCount,
}: {
  pills: Badge[];
  allBadges: Badge[];
  overflowCount: number;
}) {
  const [selected, setSelected] = useState<Badge | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  return (
    <>
      {pills.map((b) => (
        <button
          key={b.slug}
          type="button"
          onClick={() => setSelected(b)}
          title={b.name}
          className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs font-medium bg-muted hover:bg-muted/80 text-foreground/80"
        >
          <BadgeArt icon={b.icon} imageUrl={b.imageUrl} sizePx={24} className="h-3.5 w-3.5" />
          {b.name}
        </button>
      ))}
      {overflowCount > 0 ? (
        <button
          type="button"
          onClick={() => setAllOpen(true)}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted hover:bg-muted/80 text-foreground/80"
        >
          +{overflowCount} more
        </button>
      ) : null}

      <Dialog open={selected !== null} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-sm">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center text-center gap-3 pt-2 pb-1">
                <span className="flex h-28 w-28 items-center justify-center rounded-full bg-muted overflow-hidden">
                  <BadgeArt
                    icon={selected.icon}
                    imageUrl={selected.imageUrl}
                    sizePx={112}
                    className="h-10 w-10"
                  />
                </span>
                {selected.description ? (
                  <p className="text-sm text-foreground/90">{selected.description}</p>
                ) : null}
                <div className="text-xs text-muted-foreground">
                  Earned {fmt(selected.earnedAt)}
                  {selected.expiresAt ? ` · expires ${fmt(selected.expiresAt)}` : ''}
                </div>
                {rarityLabel(selected.ownedPercent) ? (
                  <div className="text-xs text-muted-foreground">
                    {rarityLabel(selected.ownedPercent)}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <BadgeModal open={allOpen} onClose={() => setAllOpen(false)} badges={allBadges} />
    </>
  );
}
